import { MapView } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import HeightIcon from '@mui/icons-material/Height';
import NearMeIcon from '@mui/icons-material/NearMe';
import PlaceIcon from '@mui/icons-material/Place';
import SpeedIcon from '@mui/icons-material/Speed';
import StoreIcon from '@mui/icons-material/Store';
import ExploreIcon from '@mui/icons-material/Explore';
import { Box, CircularProgress, Grid, Alert, Button, Paper, SvgIcon, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { blue } from '@mui/material/colors';
import { convertMetersToFeet, Display, formatLatitude, formatLongitude, GeoInformation, MapStyle, OrderInfo, Port, ReceiveStatus } from 'adoms-common-lib';
import { Auth, Geo } from 'aws-amplify';
import * as geolib from "geolib";
import React, { useEffect, useMemo } from 'react';
import { Layer, Marker, Source, NavigationControl, Popup, LineLayer } from 'react-map-gl';
import { ReactComponent as DroneIcon } from "../../asset/icons/quadcopter.svg";
import { APIConnector } from '../../connector/APIConnector';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { load } from "@loaders.gl/core";
import { KMLLoader } from '@loaders.gl/kml';
import type { FeatureCollection } from 'geojson';
import InfoRow from '../atoms/InfoRow';

type AmplifyGeoConfig = {
    geo?: {
        amazon_location_service?: AmplifyGeoOptions;
        AmazonLocationService?: AmplifyGeoOptions;
    };
};

type AmplifyGeoOptions = {
    maps?: {
        neutral: string,
        satellite: string
    };
    region: string;
};

type DroneMapViewProps = {
    order: OrderInfo | undefined
    tenantId: string | undefined
}

type GeoInformationPerFlightId = {
    flightId: string,
    geoInformationList: GeoInformation[]
};

type LatestGeoInformationPerFlightId = {
    flightId: string,
    geoInformation: GeoInformation
};

/**
 * ドローン位置情報マップを表示する
 * @param props order
 */
export const DroneMapView: React.FC<DroneMapViewProps> = (props) => {

    const amplifyConfig = Geo.configure() as AmplifyGeoConfig;
    Auth.configure();

    const geoConfig = useMemo(
        () =>
            amplifyConfig.geo?.amazon_location_service ??
            amplifyConfig.geo?.AmazonLocationService ??
            ({} as AmplifyGeoOptions),
        [amplifyConfig]
    );
    const styleProps = useMemo(
        () => ({
            height: "350px",
            position: 'relative',
            width: '100%'
        }),
        []
    );
    type MinMaxLanLng = {
        minLat: number,
        maxLat: number,
        minLng: number,
        maxLng: number
    };
    const [mapErrorMessage, setMapErrorMessage] = React.useState<string | undefined>();
    const [geoConfigMapStyle, setGeoConfigMapStyle] = React.useState<string | undefined>(geoConfig.maps?.neutral);
    const [geoInformationByOrderIdErrorMessage, setGeoInformationByOrderIdErrorMessage] = React.useState<string | undefined>();
    const [geoInformationListPerFlightIdList, setGeoInformationListPerFlightIdList] = React.useState<GeoInformationPerFlightId[]>([]);
    const [mapStyle, setMapStyle] = React.useState<MapStyle>(MapStyle.neutral);
    const [departurePortPopupInfo, setDeparturePortPopupInfo] = React.useState<Port | undefined>();
    const [arrivalPortPopupInfo, setArrivalPortPopupInfo] = React.useState<Port | undefined>();
    const [dronePopupInfo, setDronePopupInfo] = React.useState<GeoInformation | undefined>();
    const [minMaxLanLng, setMinMaxLanLng] = React.useState<MinMaxLanLng | undefined>(undefined);
    const [routeData, setRouteData] = React.useState<FeatureCollection | undefined>();
    const [hasCompletedInitialGeoFetch, setHasCompletedInitialGeoFetch] = React.useState<boolean>(false);
    const mapViewRef = React.useRef<any>(null);
    // 地図の中心点が算出できているかどうかのフラグ
    const hasInitializedRef = React.useRef<boolean>(false);
    // 地図の中心点に合わせる処理が実行されたかどうかのフラグ
    const hasAutoFittedRef = React.useRef<boolean>(false);

    const fitMapToBounds = React.useCallback((bounds: MinMaxLanLng) => {
        const map = mapViewRef.current;
        if (!map) {
            return;
        }
        // 地図コンテナのサイズを再計算し、表示ずれを防ぐ
        map.resize?.();
        map.fitBounds?.(
            [
                [bounds.minLng, bounds.minLat],
                [bounds.maxLng, bounds.maxLat]
            ],
            { padding: 60, duration: 0 }
        );
    }, []);

    const calculateBoundsFromRouteData = (routeGeoJson: FeatureCollection): MinMaxLanLng | undefined => {
        const coordinates: {
            longitude: number;
            latitude: number;
        }[] = [];

        routeGeoJson.features.forEach((feature) => {
            const geometry = feature.geometry as any;
            if (!geometry) {
                return;
            }

            if (geometry.type === "LineString") {
                geometry.coordinates.forEach((coordinate: number[]) => {
                    coordinates.push({
                        longitude: coordinate[0],
                        latitude: coordinate[1]
                    });
                });
            }
        });

        if (coordinates.length === 0) {
            return undefined;
        }

        return geolib.getBounds(coordinates);
    };

    /**
     * 画面表示時に地図の中心となる点を算出する
     * また、ドローンの位置情報を取得する
     */
    useEffect(() => {
        if (hasInitializedRef.current) {
            return;
        }

        if (props.order) {
            hasInitializedRef.current = true;
            const minMaxLanLngParam = geolib.getBounds([
                {
                    longitude: Number(props.order.departure.longitude),
                    latitude: Number(props.order.departure.latitude)
                },
                {
                    longitude: Number(props.order.arrival.longitude),
                    latitude: Number(props.order.arrival.latitude)
                },
            ]);
            if (!minMaxLanLngParam) {
                // 出発地点と到着地点の中間地点の座標取得に失敗した場合
                setMapErrorMessage("地図表示地点を取得できませんでした");
            } else {
                setMapErrorMessage(undefined);
                setMinMaxLanLng(minMaxLanLngParam);
            };
            const dataload = async () => {
                // 表示させるルートを取得する
                const kmlFilePath = "./kml/orderAndRequesterView/" + props.order?.businessPartnerId
                    + "_" + props.order?.departure.id
                    + "_" + props.order?.arrival.id + ".kml";
                try {
                    //KMLデータを読み込む
                    const res = await load(kmlFilePath, KMLLoader);
                    //KMLローダーで読み込んだデータはGeoJSON形式に変換されている
                    console.log(res);
                    //読み込んだデータを保存
                    setRouteData(res);

                    const routeBounds = calculateBoundsFromRouteData(res as FeatureCollection);
                    if (routeBounds) {
                        setMinMaxLanLng(routeBounds);
                    } else if (minMaxLanLngParam) {
                        setMinMaxLanLng(minMaxLanLngParam);
                    }
                } catch (error) {
                    console.log(error);
                    setRouteData(undefined);
                    if (minMaxLanLngParam) {
                        setMinMaxLanLng(minMaxLanLngParam);
                    }
                }
            };
            dataload();
            setHasCompletedInitialGeoFetch(false);
            fetchGeoInformationByFlightId(true);
        };
    }, [props.order]);

    useEffect(() => {
        if (!minMaxLanLng || hasAutoFittedRef.current) {
            return;
        }
        // 地図の中心点が算出できており、かつまだ自動で中心点に合わせていない場合のみ、
        // 地図を中心点に合わせる
        hasAutoFittedRef.current = true;

        // MapViewのrefからmapオブジェクトが取得できない場合があるため、
        // 0msのsetTimeoutで次のイベントループに回し、描画後にfitMapToBoundsを実行する
        const timeoutId = window.setTimeout(() => {
            fitMapToBounds(minMaxLanLng);
        }, 0);

        return () => {
            // クリーンアップ関数でtimeoutをクリアする
            window.clearTimeout(timeoutId);
        };
    }, [minMaxLanLng, fitMapToBounds]);

    /**
     * 配送が完了、またはキャンセルされていない場合、
     * 5秒間隔でドローンの位置情報を取得する
     */
    useEffect(() => {
        if (props.order?.receiveStatus !== ReceiveStatus.Received
            && props.order?.receiveStatus !== ReceiveStatus.Cancel) {
            let timeoutId: number | undefined
            // 5秒後に位置情報を再取得する（クリーンアップ時にclearTimeoutで解除）
            timeoutId = window.setTimeout(() => fetchGeoInformationByFlightId(false), 5 * 1000);
            return () => {
                if (typeof timeoutId !== "undefined") {
                    window.clearTimeout(timeoutId);
                };
            };
        }
    }, [geoInformationListPerFlightIdList]);

    /**
     * フライトIDごとのドローンの位置情報を取得する
     */
    const fetchGeoInformationByFlightId = async (isInitialFetch: boolean = false) => {
        if (document.hasFocus()) {
            // 画面がアクティブの場合
            let c: APIConnector = APIConnector.instance;
            if (props.order && props.tenantId) {
                await c.getGeoInformationByOrderId(props.order.orderID, props.tenantId).then((geoInformationPerFlightIdMap: Map<string, GeoInformation[]>) => {
                    console.log(geoInformationPerFlightIdMap);
                    setGeoInformationByOrderIdErrorMessage(undefined);
                    let geoInformationListPerFlightIdList: GeoInformationPerFlightId[] = new Array();
                    geoInformationPerFlightIdMap.forEach(function (geoInformation, flightId) {
                        if (geoInformation) {
                            let geoInformationListPerFlightId = {
                                flightId: flightId,
                                geoInformationList: geoInformation
                            };
                            geoInformationListPerFlightIdList.push(geoInformationListPerFlightId);
                        };
                    });
                    setGeoInformationListPerFlightIdList(geoInformationListPerFlightIdList);
                    if (isInitialFetch) {
                        setHasCompletedInitialGeoFetch(true);
                    }
                }).catch((error) => {
                    console.log(error);
                    setGeoInformationByOrderIdErrorMessage("ドローン位置情報を取得できませんでした。");
                    if (isInitialFetch) {
                        setHasCompletedInitialGeoFetch(true);
                    }
                });
            };
        } else {
            setGeoInformationListPerFlightIdList([...geoInformationListPerFlightIdList]);
        }
    };

    /**
     * ドローンの経路（マーカー）の色をグラデーションで表示する。
     * 先頭のマーカーはドローンのアイコンを表示させる。
     * @param locationIndex 
     */
    const handleDisplayLocationIcon = (locationIndex: number) => {

        if (locationIndex === 0) {
            return (
                <SvgIcon
                    sx={{
                        color: mapStyle === MapStyle.satellite ? "#ffffff" : "#000000",
                        stroke: mapStyle === MapStyle.satellite ? "#000000" : "#ffffff",
                        strokeWidth: "0.5px"
                    }}>
                    <DroneIcon />
                </SvgIcon >
            );
        } else {
            return (
                <FiberManualRecordIcon
                    sx={{ color: "#e06666", fontSize: 10 }} />
            );
        };
    };

    /**
     * ドローン用ポップアップを表示させる
     */
    const displayDronePopupInfo = () => {
        if (dronePopupInfo) {
            let sendTime = Display.getTimeMiniteSecondStringFromISO8601(dronePopupInfo.sendTime);
            return (
                <Popup
                    longitude={dronePopupInfo.longitude}
                    latitude={dronePopupInfo.latitude}
                    anchor="bottom"
                    onClose={() => setDronePopupInfo(undefined)}
                    style={{ zIndex: 2600 }}>
                    <Grid container direction="row">
                        <ScheduleIcon fontSize="small" sx={{ marginRight: "2px" }} />
                        <Typography variant='subtitle2'>{sendTime}</Typography>
                    </Grid>
                </Popup>
            );
        };
    };

    /**
     * ポート用ポップアップを表示させる
     */
    const displayPortPopupInfo = () => {

        if (departurePortPopupInfo) {
            return (
                <Popup
                    longitude={Number(departurePortPopupInfo.longitude)}
                    latitude={Number(departurePortPopupInfo.latitude)}
                    anchor="bottom"
                    onClose={() => setDeparturePortPopupInfo(undefined)}
                    style={{ zIndex: 2600 }}>
                    <Typography variant='subtitle2' sx={{ color: "#2196f3" }}>{"出発地"}</Typography>
                    <Typography variant='subtitle1' fontWeight={"bold"} sx={{ marginBottom: "3px" }}>
                        {departurePortPopupInfo.name}
                    </Typography>
                    <Typography variant='subtitle2'>
                        〒{departurePortPopupInfo.zipcode}
                    </Typography>
                    <Typography variant='subtitle2'>
                        {departurePortPopupInfo.prefecture}{departurePortPopupInfo.address}
                    </Typography>
                </Popup>
            );
        } else if (arrivalPortPopupInfo) {
            return (
                <Popup
                    longitude={Number(arrivalPortPopupInfo.longitude)}
                    latitude={Number(arrivalPortPopupInfo.latitude)}
                    anchor="bottom"
                    onClose={() => setArrivalPortPopupInfo(undefined)}
                    style={{ zIndex: 2600 }}>
                    <Typography variant='subtitle2' sx={{ color: "#2196f3" }}>{"到着地"}</Typography>
                    <Typography variant='subtitle1' fontWeight={"bold"} sx={{ marginBottom: "3px" }}>
                        {arrivalPortPopupInfo.name}
                    </Typography>
                    <Typography variant='subtitle2'>
                        〒{arrivalPortPopupInfo.zipcode}
                    </Typography>
                    <Typography variant='subtitle2'>
                        {arrivalPortPopupInfo.prefecture}{arrivalPortPopupInfo.address}
                    </Typography>
                </Popup>
            );
        };
    };

    /**
     * 地図の表示形式が変更された時のハンドラ
     * @param mapStyle 
     */
    const handleChangeMapStyle = (mapStyle: MapStyle) => {
        setMapStyle(mapStyle);
        if (mapStyle === MapStyle.neutral) {
            setGeoConfigMapStyle(geoConfig.maps?.neutral);
        } else if (mapStyle === MapStyle.satellite) {
            setGeoConfigMapStyle(geoConfig.maps?.satellite);
        };
    };

    const lineLayerStyle: LineLayer = {
        id: 'line',
        type: 'line',
        paint: {
            "line-color": "#ea9245",
            "line-width": 3
        }
    };

    const latestGeoInformationList = useMemo<LatestGeoInformationPerFlightId[]>(() => {
        return geoInformationListPerFlightIdList
            .map((geoInfoPerFlightId) => ({
                flightId: geoInfoPerFlightId.flightId,
                geoInformation: geoInfoPerFlightId.geoInformationList[0]
            }))
            .filter((geoInfoPerFlightId): geoInfoPerFlightId is LatestGeoInformationPerFlightId => {
                return geoInfoPerFlightId.geoInformation !== undefined;
            });
    }, [geoInformationListPerFlightIdList]);

    const displayAltitude = (altitude?: number) => {
        if (altitude === undefined || altitude === null) {
            return "ー";
        }
        const feet = convertMetersToFeet(altitude);
        return `${altitude} m / ${feet} ft`;
    };

    const displaySendTime = (sendTime?: string) => {
        const formattedSendTime = Display.getDateTimeSecondSlashStringFromISO8601(sendTime);
        if (formattedSendTime === "未定義") {
            return "ー";
        }
        return formattedSendTime;
    };

    const displayReceivedBefore = (sendTime?: string) => {
        if (!sendTime || props.order?.receiveStatus === ReceiveStatus.Received) {
            return undefined;
        }

        const sendTimeDate = new Date(sendTime);
        if (Number.isNaN(sendTimeDate.getTime())) {
            return undefined;
        }

        const now = new Date();
        const diffSeconds = Math.max(0, Math.floor((now.getTime() - sendTimeDate.getTime()) / 1000));

        if (diffSeconds <= 59) {
            return `${diffSeconds}秒前`;
        }

        if (diffSeconds <= (23 * 60 * 60 + 59 * 60)) {
            const hoursBefore = Math.max(1, Math.floor(diffSeconds / 3600));
            return `${hoursBefore}時間前`;
        }

        return undefined;
    };

    const displayHeading = (heading?: number) => {
        if (heading === undefined || heading === null) {
            return "ー";
        }
        return `${heading}°`;
    };

    const displayLatitude = (latitude?: number) => {
        if (latitude === undefined || latitude === null) {
            return "ー";
        }
        return `${latitude} (${formatLatitude(latitude)})`;
    };

    const displayLongitude = (longitude?: number) => {
        if (longitude === undefined || longitude === null) {
            return "ー";
        }
        return `${longitude} (${formatLongitude(longitude)})`;
    };

    const displaySpeed = (speed?: number) => {
        if (speed === undefined || speed === null) {
            return "ー";
        }
        const knotPerMetersPerSecond = 1.94384;
        const speedKt = Number((speed * knotPerMetersPerSecond).toFixed(2));
        return `${speed} m/s / ${speedKt} kt`;
    };

    const displayLabel = (icon: React.ReactElement, label: string) => {
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {icon}
                {label}
            </Box>
        );
    };

    return (
        <React.Fragment>
            {
                mapErrorMessage ?
                    <Paper sx={{
                        padding: "10px",
                        marginBottom: "10px"
                    }} elevation={1} >
                        <Alert severity="error">{mapErrorMessage}</Alert>
                    </Paper>
                    : undefined
            }
            {
                geoInformationByOrderIdErrorMessage ?
                    <Paper sx={{
                        padding: "10px",
                        margin: "10px"
                    }} elevation={1} >
                        <Alert severity="error">{geoInformationByOrderIdErrorMessage}</Alert>
                    </Paper>
                    : undefined
            }
            {
                props.order && minMaxLanLng ?
                    <MapView
                        ref={mapViewRef}
                        initialViewState={
                            props.order.departure.id
                                === props.order.arrival.id && !routeData ?
                                {
                                    latitude: Number(props.order.departure.latitude),
                                    longitude: Number(props.order.departure.longitude),
                                    zoom: props.order.businessPartnerId === "AIRWINGS" ? 13 : 15
                                }
                                : {
                                    bounds:
                                        [
                                            [minMaxLanLng.minLng, minMaxLanLng.minLat],
                                            [minMaxLanLng.maxLng, minMaxLanLng.maxLat]],
                                    fitBoundsOptions: {
                                        padding: 60
                                    }
                                }}
                        style={styleProps as React.CSSProperties}
                        onError={error => {
                            console.log(error);
                            if (error) {

                            }
                            setMapErrorMessage("地図情報を取得できませんでした。ページを更新してください。");
                        }}
                        onLoad={() => fitMapToBounds(minMaxLanLng)}
                        mapStyle={geoConfigMapStyle}>
                        <Marker
                            longitude={Number(props.order.departure.longitude)}
                            latitude={Number(props.order.departure.latitude)}
                            onClick={e => {
                                e.originalEvent.stopPropagation();
                                setDeparturePortPopupInfo(props.order?.departure);
                            }}>
                            <Avatar sx={{ height: 30, width: 30, backgroundColor: blue[500] }}>
                                <StoreIcon fontSize='small' />
                            </Avatar>
                        </Marker>
                        <Marker
                            longitude={Number(props.order.arrival.longitude)}
                            latitude={Number(props.order.arrival.latitude)}
                            onClick={e => {
                                e.originalEvent.stopPropagation();
                                setArrivalPortPopupInfo(props.order?.arrival);
                            }}>
                            <PlaceIcon fontSize='large' sx={{ color: "#dc143c" }} />
                        </Marker>
                        {geoInformationListPerFlightIdList.map((geoInformationListPerDroneIdId, droneIndex) => (
                            (geoInformationListPerDroneIdId.geoInformationList).map((geoInformation, locationIndex) => (
                                <Marker
                                    key={`marker-${droneIndex}-${locationIndex}`}
                                    longitude={geoInformation.longitude}
                                    latitude={geoInformation.latitude}
                                    style={{ zIndex: 10 + locationIndex }}
                                    onClick={e => {
                                        e.originalEvent.stopPropagation();
                                        setDronePopupInfo(geoInformation)
                                    }}
                                >
                                    {handleDisplayLocationIcon(locationIndex)}
                                </Marker>
                            ))
                        ))}
                        {displayPortPopupInfo()}
                        {displayDronePopupInfo()}
                        {mapStyle === MapStyle.satellite ?
                            <Button
                                sx={{ margin: "10px", minWidth: "0px" }}
                                size='small'
                                variant="contained"
                                onClick={(e) => handleChangeMapStyle(MapStyle.neutral)}
                            >
                                地形
                            </Button>
                            :
                            <Button
                                sx={{ margin: "10px", minWidth: "0px" }}
                                size='small'
                                variant="contained"
                                onClick={(e) => handleChangeMapStyle(MapStyle.satellite)}
                            >
                                衛星
                            </Button>
                        }
                        <NavigationControl style={{ margin: "10px" }} />
                        {routeData ?
                            <Source id="route" type="geojson" data={routeData}>
                                <Layer {...lineLayerStyle} />
                            </Source>
                            : undefined}
                        <Box
                            sx={{
                                position: "absolute",
                                left: "10px",
                                bottom: "10px",
                                width: { xs: "calc(100% - 20px)", sm: "360px" },
                                maxHeight: { xs: "48%", sm: "260px" },
                                overflowY: "auto",
                                zIndex: 2700
                            }}>
                            <Paper
                                sx={{
                                    padding: "8px",
                                    borderColor: "grey.200",
                                    backgroundColor: "rgba(255, 255, 255, 0.95)"
                                }}
                                elevation={2}
                                variant='outlined'>
                                <Grid container spacing={1} alignItems="center" sx={{ marginBottom: "4px" }}>
                                    <Grid item>
                                        <Avatar sx={{ bgcolor: blue[400], width: 28, height: 28 }}>
                                            <GpsFixedIcon sx={{ fontSize: 16 }} />
                                        </Avatar>
                                    </Grid>
                                    <Grid item xs>
                                        <Typography variant='subtitle1' fontWeight={"bold"} sx={{ color: "#2196f3" }}>
                                            ドローン位置情報
                                        </Typography>
                                        <Typography variant='subtitle2' sx={{ color: "#bda4a4" }}>
                                            直近の位置情報を表示しています
                                        </Typography>
                                    </Grid>
                                </Grid>
                                {
                                    !hasCompletedInitialGeoFetch ?
                                        <Grid container justifyContent="center" alignItems="center" sx={{ py: 2 }}>
                                            <CircularProgress size={24} />
                                        </Grid>
                                        : latestGeoInformationList.length === 0 ?
                                        <Typography variant='body2'>最新位置情報はありません。</Typography>
                                        : latestGeoInformationList.map((latestGeoInformationPerFlightId, index) => (
                                            <Paper
                                                key={`latest-geo-overlay-${latestGeoInformationPerFlightId.flightId}-${index}`}
                                                sx={{
                                                    padding: "8px",
                                                    marginBottom: index === latestGeoInformationList.length - 1 ? "0px" : "6px",
                                                    borderColor: "grey.200",
                                                }}
                                                elevation={0}
                                                variant='outlined'>
                                                <InfoRow
                                                    label={displayLabel(<PlaceIcon sx={{ fontSize: 14, color: blue[400] }} />, "緯度")}
                                                    value={displayLatitude(latestGeoInformationPerFlightId.geoInformation.latitude)}
                                                />
                                                <InfoRow
                                                    label={displayLabel(<NearMeIcon sx={{ fontSize: 14, color: blue[400] }} />, "経度")}
                                                    value={displayLongitude(latestGeoInformationPerFlightId.geoInformation.longitude)}
                                                />
                                                <InfoRow
                                                    label={displayLabel(<HeightIcon sx={{ fontSize: 14, color: blue[400] }} />, "高度")}
                                                    value={displayAltitude(latestGeoInformationPerFlightId.geoInformation.altitude)}
                                                />
                                                <InfoRow
                                                    label={displayLabel(<SpeedIcon sx={{ fontSize: 14, color: blue[400] }} />, "速度")}
                                                    value={displaySpeed(latestGeoInformationPerFlightId.geoInformation.speed)}
                                                />
                                                <InfoRow
                                                    label={displayLabel(<ExploreIcon sx={{ fontSize: 14, color: blue[400] }} />, "方位角")}
                                                    value={displayHeading(latestGeoInformationPerFlightId.geoInformation.heading)}
                                                />
                                                {displayReceivedBefore(latestGeoInformationPerFlightId.geoInformation.sendTime) ?
                                                    <InfoRow
                                                        label={displayLabel(<ScheduleIcon sx={{ fontSize: 14, color: blue[400] }} />, "受信")}
                                                        value={displayReceivedBefore(latestGeoInformationPerFlightId.geoInformation.sendTime)}
                                                    /> : undefined}
                                            </Paper>
                                        ))
                                }
                            </Paper>
                        </Box>
                    </MapView>
                    : undefined
            }
        </React.Fragment >
    )
}